import React, { useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import SlideBase from './SlideBase'
import WrappedImageBackground from '../WrappedImageBackground'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'

const SLIDE_ID = 'top-offer' as const
const MAX_INCLUSIONS = 3

function TopOfferSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Use totals offer when in totals-only mode or user has no top offer
  const userTopOffer = data?.topOffer
  const totalsTopOffer = totalsData?.topOffer
  const useTotalsFallback = isTotalsOnlyMode || !userTopOffer?.imageId
  const topOffer = useTotalsFallback ? totalsTopOffer : userTopOffer
  const visibleInclusions = topOffer?.inclusions.slice(0, MAX_INCLUSIONS) ?? []

  // Extract ternary heading variants as constants
  const headingTotalsFallback = intl.formatMessage({
    id: '9YkrMEnpY2vFn_ihV4qp',
    defaultMessage: 'The most loved hotel of 2025 was',
    description: 'Top offer slide heading for totals-only mode on the hotel page',
  })
  const headingAlternative = intl.formatMessage({
    id: 'HYz6qVOsKjYqVGDu7KUA',
    defaultMessage: 'Your best-matched hotel is',
    description: 'Top offer slide heading for alternative matched offer',
  })
  const headingTopViewed = intl.formatMessage({
    id: 'pHBV97c-ZV86DB-Q-lwv',
    defaultMessage: 'The hotel you loved the most was',
    description: 'Top offer slide heading for top viewed offer on the hotel page',
  })

  // Extract ternary caption variants as constants
  const titleTotalsFallback = intl.formatMessage({
    id: 'JuMjxM4aJtpldq5rjx_W',
    defaultMessage: 'Most Loved Offer',
    description: 'Top offer slide caption for totals-only mode in the wrapped summary',
  })
  const titleAlternative = intl.formatMessage({
    id: '1LW8QJgw8V_aO3-5d3en',
    defaultMessage: 'Top Matched Offer',
    description: 'Top offer slide caption for alternative matched offer',
  })
  const titleTopViewed = intl.formatMessage({
    id: '6V40Q3E7Oq7UPmLN92ZY',
    defaultMessage: 'Top Viewed Offer',
    description: 'Top offer slide caption for top viewed offer in the wrapped summary',
  })

  // Adjust wording for totals mode
  const heading = useTotalsFallback
    ? headingTotalsFallback
    : (topOffer?.isAlternative ? headingAlternative : headingTopViewed)
  const title = useTotalsFallback ? titleTotalsFallback : (topOffer?.isAlternative ? titleAlternative : titleTopViewed)

  // Memoize background to prevent recreation
  const background = useMemo(() => {
    if (!topOffer?.imageId) return undefined
    return (
      <WrappedImageBackground
        imageId={topOffer?.imageId}
        alt={topOffer?.name}
        kenBurnsVariant="zoom"
        isActive={isCurrentSlide && !isPaused}
      />
    )
  }, [topOffer?.imageId, topOffer?.name, isCurrentSlide, isPaused])

  // Need a top offer from either user data or totals to render
  if (!topOffer) return null

  return (
    <SlideBase background={background}>
      <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">{title}</Caption>
            <Heading variant="heading5"><b>{heading}</b></Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">{topOffer?.propertyName}</Heading>
            {!useTotalsFallback && (
              <BodyText variant="medium">
                <FormattedMessage
                  id="lWKCK_nteElsFbg2gKCR"
                  defaultMessage="{location} • Viewed <b>{viewCount} times</b>"
                  description="Top offer slide location and view count text in the wrapped summary"
                  values={{
                    location: topOffer?.location,
                    viewCount: topOffer?.viewCount ?? 0,
                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  }}
                />
              </BodyText>
            )}
            {useTotalsFallback && topOffer?.location && (
              <BodyText variant="medium">
                {topOffer.location}
              </BodyText>
            )}
          </Group>
          <Group direction="vertical" gap={4}>
            {visibleInclusions.length > 0 && <OfferPagePackageInclusionsList>
              {visibleInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="small" />)}
            </OfferPagePackageInclusionsList>}
          </Group>
          <InteractiveZone>
            <TextButton
              shape="round"
              fit="full-width"
              kind="secondary"
              variant="default"
              to={`/offer/${topOffer?.id}`}
              target="_blank"
            >
              {intl.formatMessage({
                id: 'InLqxw6HqqWSKhgvkOFZ',
                defaultMessage: 'View offer',
                description: 'Button label to view the top offer on the year-end wrapped summary slide',
              })}
            </TextButton>
          </InteractiveZone>
        </Group>
      </FadeBackground>
    </SlideBase>
  )
}

export default React.memo(TopOfferSlide)
